<template>
  <div class="visitor-form p-6 flex flex-col h-full">
    <div class="text-center mb-8">
      <div class="logo-container mx-auto w-16 h-16 mb-4">
        <img 
          src="/orbitar_chat_logo.png" 
          alt="Orbitar AI"
          class="h-16 w-16 rounded-full object-cover"
        />
      </div>
      <h1 class="text-2xl font-semibold text-gray-800">Welcome to Chat</h1>
      <p class="text-gray-600 mt-2">Please provide your information to start chatting</p>
    </div>
    
    <form @submit.prevent="handleSubmit" class="flex-1 flex flex-col">
      <div class="mb-4">
        <label for="name" class="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input 
          id="name"
          v-model="name" 
          type="text" 
          :required="initialFormFields.some(field => field.key === 'name' && field.required)"
          :disabled="loading"
          placeholder="Your name"
          class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          :class="{ 'opacity-70': loading }"
        />
      </div>
      
      <div class="mb-4">
        <label for="email" class="block text-sm font-medium text-gray-700 mb-1">Email</label>
        <input 
          id="email"
          v-model="email" 
          type="email"
          :required="initialFormFields.some(field => field.key === 'email' && field.required)" 
          :disabled="loading"
          placeholder="your.email@example.com"
          class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          :class="{ 'opacity-70': loading }"
        />
      </div>

      <!-- Dynamic form fields from widget configuration -->
      <div v-for="field in customFields" :key="field.key" class="mb-4">
        <label :for="field.key" class="block text-sm font-medium text-gray-700 mb-1">{{ field.label }}</label>
        <input 
          :id="field.key"
          v-model="customFieldValues[field.key]" 
          :type="field.type || 'text'" 
          :required="field.required"
          :disabled="loading"
          :placeholder="field.placeholder"
          class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          :class="{ 'opacity-70': loading }"
        />
      </div>
      
      <div class="error-message mb-4 text-sm text-red-600" v-if="errorMessage">
        {{ errorMessage }}
      </div>
      
      <!-- Mobile-safe button container with extra spacing -->
      <div class="mt-auto mobile-button-container">
        <button 
          type="submit" 
          class="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-white bg-primary hover:bg-opacity-90 focus:outline-none mobile-submit-button"
          :disabled="loading || !isValid"
          :class="{ 'opacity-70 cursor-not-allowed': loading || !isValid }"
        >
          <span v-if="loading">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Starting chat...
          </span>
          <span v-else>Start Chat</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useChatConfigStore } from '@/store/config';
import { generateUUID } from '@/utils/uuid';

interface VisitorInfo {
  name?: string;
  email?: string;
  visitorId?: string;
  metadata?: Record<string, any>;
}

const emit = defineEmits<{
  (event: 'submit', visitorInfo: VisitorInfo): void;
}>();

const props = defineProps<{
  widgetId: string;
}>();

// Form state
const name = ref('');
const email = ref('');
const customFieldValues = ref<Record<string, string>>({});
const loading = ref(false);
const errorMessage = ref('');
const initialFormFields = ref<{ key: string; label: string; required: boolean; type?: string; placeholder?: string }[]>([]);

// Get configuration store
const configStore = useChatConfigStore();

// Filter out standard fields (name, email) that are handled separately
const customFields = computed(() => {
  return initialFormFields.value.filter(field => field.key !== 'name' && field.key !== 'email');
});

// Computed properties
const isValid = computed(() => {
  // Check required standard fields
  const nameRequired = initialFormFields.value.some(field => field.key === 'name' && field.required);
  const emailRequired = initialFormFields.value.some(field => field.key === 'email' && field.required);
  
  if ((nameRequired && !name.value.trim()) || (emailRequired && !email.value.trim())) {
    return false;
  }
  
  // Check required custom fields
  for (const field of customFields.value) {
    if (field.required && !customFieldValues.value[field.key]?.trim()) {
      return false;
    }
  }
  
  return true;
});

// Methods
const handleSubmit = async () => {
  if (!isValid.value || loading.value) return;
  
  loading.value = true;
  errorMessage.value = '';
  
  try {
    // Combine all field values into visitor info object
    const visitorInfo: VisitorInfo = {
      name: name.value.trim() || undefined,
      email: email.value.trim() || undefined,
      visitorId: generateUUID(),
      metadata: {}
    };
    
    // Add custom fields to metadata
    for (const field of customFields.value) {
      if (customFieldValues.value[field.key]) {
        visitorInfo.metadata![field.key] = customFieldValues.value[field.key];
      }
    }
    
    // Emit submit event with visitor info
    emit('submit', visitorInfo);
  } catch (error) {
    errorMessage.value = 'An error occurred. Please try again.';
    loading.value = false;
  }
};

// Load form configuration on mount
onMounted(async () => {
  try {
    // Get form fields from config store, but convert id to key to match our component's expected format
    const storeFields = configStore.initialFormFields || [];
    initialFormFields.value = storeFields.map(field => ({
      key: field.id, // Convert id to key
      label: field.label,
      required: field.required,
      type: field.type,
      placeholder: '', // Add an empty placeholder
    })) || [
      { key: 'name', label: 'Name', required: true },
      { key: 'email', label: 'Email', required: true }
    ];
  } catch (error) {
    console.error('[Orbitar Chat] Error loading form configuration:', error);
    errorMessage.value = 'Could not load form configuration.';
  }
});
</script>

<style scoped>
/* Component-specific styles can go here */
</style> 